import React, { useEffect, useState, useRef, useCallback } from "react";
import { Form, Spinner } from "react-bootstrap";
import { ChevronDown } from "lucide-react";
import AutocompleteField from "./AutocompleteField";
import * as refDataCache from "../utils/refDataCache";
import { pickField, extractSecondary, dedupeSecondary } from "../utils/fieldHelpers";

// =============================================================================
// SearchableSelect — a dropdown that *looks* like a <select> (chevron, click to
// open the full list) but filters as you type. Selection-only: typing only
// narrows the list; a value not in the list is never committed.
//
// Data comes from refDataCache: the whole scoped list is loaded ONCE and reused
// for the session (usually already warmed by prefetchForSession()), so there
// are no per-keystroke network calls. Filtering is client-side.
//
// Drop-in compatible with AutocompleteField's props. The config adds:
//   allowFreeText: true -> typed text is committed as the value too (a ZIP),
//               the list is only a shortcut; default stays selection-only
//   preload:    true  -> this preloaded-dropdown behaviour
//               false -> delegate to the live AutocompleteField (used for the
//                        unbounded D99-admin "all customers" case)
//   descriptor: { key, fetcher } from refDataCache (the dataset to load)
//   displayField / valueField / secondaryField / minChars — same as before.
// =============================================================================
function SearchableSelect({
  config,
  value,
  onChange,
  onSelectItem,
  disabled,
  placeholder,
  required,
  size,
  maxLength,
  inputWidth,
  variant,
  label,
  secondaryText,
}) {
  // Live fallback — keep the existing autocomplete for unbounded scopes.
  if (!config || config.preload === false || (!config.descriptor && !config.options)) {
    return (
      <AutocompleteField
        config={config}
        value={value}
        onChange={onChange}
        onSelectItem={onSelectItem}
        disabled={disabled}
        placeholder={placeholder}
        required={required}
        size={size}
        maxLength={maxLength}
        inputWidth={inputWidth}
        variant={variant}
        label={label}
      />
    );
  }
  return (
    <PreloadedSelect
      config={config}
      value={value}
      onChange={onChange}
      onSelectItem={onSelectItem}
      disabled={disabled}
      placeholder={placeholder}
      required={required}
      size={size}
      maxLength={maxLength}
      inputWidth={inputWidth}
      variant={variant}
      label={label}
      secondaryText={secondaryText}
    />
  );
}

function PreloadedSelect({
  config, value, onChange, onSelectItem, disabled, placeholder, required, size, maxLength, inputWidth,
  variant, label, secondaryText,
}) {
  // Static options (a local array passed in config.options) bypass refDataCache
  // entirely — for caller-supplied lists that aren't a cached reference dataset.
  const staticOptions = config.options || null;
  const { key, fetcher } = config.descriptor || {};

  const [options, setOptions] = useState(() => staticOptions || (key ? refDataCache.peek(key) : null) || []);
  const [inputValue, setInputValue] = useState(value || "");
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const [displayedSecondary, setDisplayedSecondary] = useState("");
  // `dirty` = the user has typed since opening, so the input is a filter.
  // While not dirty, the full list shows (the "click to browse" affordance).
  const [dirty, setDirty] = useState(false);

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Find the cached option whose valueField matches a given value.
  const findByValue = useCallback(
    (val) => {
      if (val == null || val === "") return undefined;
      const needle = String(val).toLowerCase();
      return options.find(
        (it) => String(pickField(it, config.valueField) ?? "").toLowerCase() === needle
      );
    },
    [options, config.valueField]
  );

  // Subscribe to cache updates for this key (background load finishing, or the
  // cache being cleared on logout). Re-seed options whenever they land.
  useEffect(() => {
    if (staticOptions) { setOptions(staticOptions); return undefined; }
    setOptions(refDataCache.peek(key) || []);
    const unsub = refDataCache.subscribe(key, () => {
      setOptions(refDataCache.peek(key) || []);
    });
    return unsub;
  }, [key, staticOptions]);

  // Keep the box text + secondary label in sync with the committed `value`
  // (e.g. when it's defaulted externally, or filled by a sibling reverse-pick).
  // The description prefers the cached list match; when the list isn't cached
  // yet it falls back to the caller-supplied `secondaryText` (e.g. the name
  // that came down with the login response) so it shows instantly with no
  // extra request. Skipped while the user is actively filtering.
  useEffect(() => {
    if (dirty) return;
    setInputValue(value || "");
    if (!value) { setDisplayedSecondary(""); return; }
    const match = findByValue(value);
    setDisplayedSecondary(
      match ? extractSecondary(match, config.secondaryField) : (secondaryText || "")
    );
  }, [value, dirty, findByValue, config.secondaryField, secondaryText]);

  // Close on outside click — revert the filter text to the committed value.
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        closeAndRevert();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const ensureLoaded = useCallback(() => {
    if (staticOptions) return; // local options — nothing to fetch
    if (refDataCache.peek(key) !== undefined) return; // already cached
    setIsLoading(true);
    refDataCache
      .load(key, fetcher)
      .then((arr) => setOptions(arr))
      .catch(() => { /* leave list empty; user can close + retry */ })
      .finally(() => setIsLoading(false));
  }, [key, fetcher, staticOptions]);

  const open = () => {
    if (disabled) return;
    // Only reset the filter when opening from closed — re-focusing/clicking an
    // already-open field shouldn't wipe what the user has typed.
    if (!isOpen) {
      setDirty(false);
      setHighlightIdx(-1);
      setIsOpen(true);
    }
    ensureLoaded();
  };

  const closeAndRevert = () => {
    setIsOpen(false);
    setDirty(false);
    // Selection-only: discard any unmatched filter text, restore the committed
    // value's display.
    setInputValue(value || "");
  };

  const handleInputChange = (e) => {
    const v = e.target.value;
    setInputValue(v);
    setDirty(true);
    setHighlightIdx(-1);
    // The typed text is now a filter, not the committed value — drop the
    // description shown beside it until a new item is picked.
    setDisplayedSecondary("");
    // Emptying the box clears the committed selection: without this, this
    // selection-only control never told the parent the value changed, so
    // deleting a field's text did nothing (and closeAndRevert snapped the old
    // value back on click-away). Non-empty text stays selection-only — it only
    // narrows the list until an item is picked.
    if (v === "" && value) onChange("");
    // config.allowFreeText (e.g. a ZIP): the typed text IS the value — commit it
    // as the user types, so a click-away keeps it instead of reverting.
    else if (config.allowFreeText) onChange(v);
    if (!isOpen) { setIsOpen(true); ensureLoaded(); }
  };

  const handleSelect = (item) => {
    const val = pickField(item, config.valueField) ?? "";
    onChange(String(val));
    setInputValue(String(val));
    setDisplayedSecondary(extractSecondary(item, config.secondaryField));
    setDirty(false);
    setIsOpen(false);
    onSelectItem?.(item);
  };

  // Filtered view: full list until the user types, then a contains-match across
  // display / value / secondary.
  const filtered = (() => {
    // config.pinnedOptions — rows that always sit at the TOP of the list and are
    // never filtered out (an escape hatch to a different input mode, e.g. the
    // quote wizard's "Individual components"). Undefined for every other caller,
    // so behaviour elsewhere is unchanged.
    const pinned = config.pinnedOptions || [];
    const base = (() => {
      if (!dirty || !inputValue) return options;
      const q = inputValue.toLowerCase();
      return options.filter((it) => {
        const disp = String(pickField(it, config.displayField) ?? "").toLowerCase();
        const valTxt = String(pickField(it, config.valueField) ?? "").toLowerCase();
        const sec = extractSecondary(it, config.secondaryField).toLowerCase();
        return disp.includes(q) || valTxt.includes(q) || sec.includes(q);
      });
    })();
    return pinned.length ? [...pinned, ...base] : base;
  })();

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!isOpen) { open(); return; }
      setHighlightIdx((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      if (isOpen && highlightIdx >= 0 && filtered[highlightIdx]) {
        e.preventDefault();
        handleSelect(filtered[highlightIdx]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeAndRevert();
    }
  };

  const resolvedInputWidth = inputWidth
    ? inputWidth
    : (maxLength ? `calc(${maxLength}ch + 2.5rem)` : "14ch");

  const isCard = variant === "card";

  // A committed selection that isn't currently being edited (not open, not
  // mid-type) renders its code + description together in a single overlay line
  // INSIDE the box. While this shows, the raw input text is hidden so the code
  // and description can never overlap; focusing/typing restores the input.
  const showSelectedDisplay =
    !isCard && !!value && !dirty && !isOpen &&
    !!displayedSecondary && !config?.hideSecondary;

  // Toggle the dropdown without stealing the input's focus/blur semantics.
  const toggleOpen = (e) => {
    e.preventDefault();
    if (disabled) return;
    if (isOpen) closeAndRevert();
    else open();
  };

  // Clicking anywhere on the card opens the dropdown and focuses the input so
  // the user can immediately type to filter. (The chevron handles its own
  // toggle and stops propagation so it can still close an open list.)
  const openFromCard = () => {
    if (disabled) return;
    open();
    if (inputRef.current) inputRef.current.focus();
  };

  // Chevron / spinner at the given pixel size — the dropdown affordance.
  const caretIcon = (sz) =>
    isLoading ? (
      <Spinner animation="border" size="sm" style={{ width: sz, height: sz }} />
    ) : (
      <ChevronDown
        size={sz}
        style={{ transition: "transform 0.15s", transform: isOpen ? "rotate(180deg)" : "none" }}
      />
    );

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      {isCard ? (
        // Card variant — looks like the "selected" option card: eyebrow label,
        // the code as the (searchable) value, and the description beside it.
        <div
          className={
            "qw-select-card" +
            (value ? " selected" : "") +
            (isOpen ? " open" : "") +
            (disabled ? " disabled" : "")
          }
          onClick={openFromCard}
        >
          {label && <div className="qw-select-eyebrow">{label}</div>}
          <div className="qw-select-row">
            <input
              ref={inputRef}
              className="qw-select-input"
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={open}
              onClick={open}
              disabled={disabled}
              placeholder={placeholder}
              required={required}
              maxLength={maxLength || undefined}
              autoComplete="off"
              // When a value is committed, shrink the input to the code's width
              // so the description sits right beside it. While empty or typing,
              // let it fill the row.
              style={
                value && !dirty
                  ? { flex: "0 0 auto", width: `${Math.max(String(inputValue).length, 2) + 1}ch` }
                  : undefined
              }
            />
            {displayedSecondary && !config?.hideSecondary && (
              <span className="qw-select-desc" title={displayedSecondary}>
                {displayedSecondary}
              </span>
            )}
          </div>
          {/* Bigger chevron with a generous hit area, vertically centered over
              the whole card. Stops propagation so it toggles (incl. close)
              independently of the card's click-to-open. */}
          <span
            className="qw-select-caret"
            onMouseDown={toggleOpen}
            onClick={(e) => e.stopPropagation()}
            style={{ cursor: disabled ? "default" : "pointer" }}
          >
            {caretIcon(26)}
          </span>
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ position: "relative", flex: "0 0 auto", width: resolvedInputWidth }}>
            <Form.Control
              size={size}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={open}
              onClick={open}
              disabled={disabled}
              placeholder={placeholder}
              required={required}
              maxLength={maxLength || undefined}
              autoComplete="off"
              // Hide the raw input text while the committed code+description
              // overlay is shown, so the two never overlap. Editing restores it.
              style={{ paddingRight: 30, ...(showSelectedDisplay ? { color: "transparent" } : null) }}
            />
            {/* Committed selection shown INSIDE the box on one line: the code,
                then the description. Keeps the filter row a constant height (no
                wrap-below, so the search fields never shift) and cannot overlap
                because the underlying input text is hidden while this renders.
                pointerEvents:none lets a click fall through to focus/open the
                field for editing. */}
            {showSelectedDisplay && (
              <div
                style={{
                  position: "absolute", left: 12, right: 30, top: 0, bottom: 0,
                  display: "flex", alignItems: "center", gap: 8,
                  pointerEvents: "none", overflow: "hidden",
                }}
              >
                <span style={{ fontWeight: 600, color: "#0F172A", flex: "0 0 auto", whiteSpace: "nowrap" }}>
                  {inputValue}
                </span>
                <span
                  title={displayedSecondary}
                  style={{
                    color: "#0052A5", fontSize: 12, fontWeight: 500, minWidth: 0,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}
                >
                  {displayedSecondary}
                </span>
              </div>
            )}
            <div
              onMouseDown={toggleOpen}
              style={{
                position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                display: "flex", alignItems: "center",
                cursor: disabled ? "default" : "pointer", color: "#6c757d",
              }}
            >
              {caretIcon(16)}
            </div>
          </div>
        </div>
      )}

      {isOpen && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 2000,
          background: "#fff", border: "1px solid #dee2e6", borderRadius: "0 0 6px 6px",
          maxHeight: "240px", overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}>
          {filtered.length === 0 ? (
            <div style={{ padding: "10px 12px", fontSize: 13, color: "#888" }}>
              {isLoading ? "Loading…" : "No matches"}
            </div>
          ) : (
            filtered.map((item, idx) => {
              const display = pickField(item, config.displayField);
              const secondary = extractSecondary(item, config.secondaryField);
              return (
                <div
                  key={idx}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setHighlightIdx(idx)}
                  style={{
                    padding: isCard ? "10px 16px" : "8px 12px",
                    cursor: "pointer",
                    backgroundColor: idx === highlightIdx ? "#e8f0fe" : "#fff",
                    borderBottom: idx < filtered.length - 1 ? "1px solid #f0f0f0" : "none",
                    display: "grid",
                    gridTemplateColumns: isCard ? "minmax(110px, auto) minmax(0, 1fr)" : "minmax(92px, auto) minmax(0, 1fr)",
                    gap: 14, alignItems: "baseline",
                  }}
                >
                  {/* Option A — an aligned code column, not space-between.
                     `justify-content: space-between` put the code at the far
                     left of a 1500px field and its name at the far right, ~1350px
                     apart, with both in identical 700/16px type so neither read
                     as primary. Right-alignment is for NUMBERS scanned down a
                     column; a name belongs beside its code. A shared first track
                     keeps codes aligned vertically — the scannability the old
                     layout was reaching for — while the name starts 14px later.
                     That track is `minmax(floor, auto)`, NOT a fixed width: grid
                     sizes it to the widest code in the list, so a long one like
                     "Individual components" prints in full instead of clipping to
                     "Individual com…", and a list of 5-char territory codes stays
                     tight instead of padding out to a cap nothing reaches. The
                     maxWidth on the span is the backstop — one freak part number
                     can't shove every name across the field, which is the exact
                     complaint this layout was built to answer. */}
                  <span style={isCard
                    ? { fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontWeight: 600, color: "#4338CA", fontSize: 14, marginLeft: 0, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }
                    : { fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontWeight: 600, color: "#4338CA", fontSize: 12.5, marginLeft: 0, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {display}
                  </span>
                  {dedupeSecondary(display, secondary) && (
                    <span style={isCard
                      ? { color: "#0F172A", fontSize: 15, marginLeft: 0, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }
                      : { color: "#475569", fontSize: 12.5, marginLeft: 0, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {dedupeSecondary(display, secondary)}
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
